// Built-ins
import { useEffect, useState } from "react";

// Icons
import { FiEdit } from "react-icons/fi";

// Modules
import axios from "axios";
import Swal from "sweetalert2";
import { Modal, Form } from "react-bootstrap";
import GuardedModal from "../common/GuardedModal";
import { Button } from "semantic-ui-react";
import PhoneInput from "react-phone-input-2";
import 'react-phone-input-2/lib/bootstrap.css';

// Globals
import { headers } from "../global_vars";
import SelectWithInlineCreate from "../common/SelectWithInlineCreate";
import AutocompleteSelect from "../common/AutocompleteSelect";
import AddProfessionModal from "./create/add_profession";
import AddCityModal from "./create/add_city";
import { cityOptionLabel } from "../common/cityOptionLabel";
import {
  formatPhoneForApi,
  isOptionalPhoneValid,
  isRequiredPhoneValid,
} from "../../utils/phoneInput";
import RichTextEditor from "../common/RichTextEditor";
import { htmlForApi } from "../../utils/richText";

const UPDATE_PRO = "https://ultima.icsgr.com/api/data_management/professional/";

function getAuthHeaders() {
  return { ...headers, "Authorization": "Token " + localStorage.getItem("userToken") };
}

export function EditProfessionalFullnameModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [fullname, setFullname] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setFullname(professional.fullname || ""); }, [show, professional]);
  const isValid = fullname.trim().length >= 2 && fullname.trim().length <= 40;

  const handleSave = async () => {
    if (!isValid) return; setBusy(true);
    try {
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", { fullname: fullname.trim() }, { headers: getAuthHeaders() });
      Swal.fire("Success", "Fullname updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update fullname";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Full name</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Full name</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Full name *:</Form.Label>
            <Form.Control value={fullname} onChange={(e) => setFullname(e.target.value)} maxLength={40} placeholder="2–40 chars" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Full name is required (2–40 chars)."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalProfessionModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [profession, setProfession] = useState("");
  const [professions, setProfessions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addProfessionOpen, setAddProfessionOpen] = useState(false);

  useEffect(() => { if (show) { setProfession(professional.profession?.profession_id || ""); load(); } }, [show, professional]);
  const load = async () => {
    try {
      const res = await axios.get("https://ultima.icsgr.com/api/administration/all_professions/", { headers: getAuthHeaders() });
      setProfessions(res?.data?.all_professions || []);
    } catch { setProfessions([]); }
  };
  const isValid = profession.trim().length > 0;

  const handleSave = async () => {
    if (!isValid) return; setBusy(true);
    try {
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", { profession_id: profession }, { headers: getAuthHeaders() });
      Swal.fire("Success", "Profession updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update profession";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Profession</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Profession</Modal.Title></Modal.Header>
        <Modal.Body>
          <SelectWithInlineCreate
            label="Profession"
            required
            value={profession}
            onChange={setProfession}
            placeholderOption="Select Profession"
            onAddClick={() => setAddProfessionOpen(true)}
            overviewLinkEntity="profession"
          >
            {professions.map(p => (
              <option key={p.profession_id} value={p.profession_id} title={p.profession_id}>{p.title}</option>
            ))}
          </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Profession is required."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
      <AddProfessionModal
        nested
        hideTrigger
        show={addProfessionOpen}
        onHide={() => setAddProfessionOpen(false)}
        onProfessionCreated={async (data) => {
          await load();
          const id = data?.profession_id;
          if (id) setProfession(id);
          setAddProfessionOpen(false);
        }}
      />
    </>
  );
}

export function EditProfessionalCityModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [city, setCity] = useState("");
  const [cities, setCities] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addCityOpen, setAddCityOpen] = useState(false);

  useEffect(() => { if (show) { setCity(professional.city?.city_id || ""); load(); } }, [show, professional]);
  const load = async () => {
    try {
      const res = await axios.get("https://ultima.icsgr.com/api/regions/all_cities/", { headers: getAuthHeaders() });
      setCities(res?.data?.all_cities || []);
    } catch { setCities([]); }
  };
  const isValid = city.trim().length > 0;

  const handleSave = async () => {
    if (!isValid) return; setBusy(true);
    try {
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", { city_id: city }, { headers: getAuthHeaders() });
      Swal.fire("Success", "City updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update city";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit City</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit City</Modal.Title></Modal.Header>
        <Modal.Body>
          <SelectWithInlineCreate
            label="City"
            required
            value={city}
            onChange={setCity}
            placeholderOption="Select City"
            onAddClick={() => setAddCityOpen(true)}
            overviewLinkEntity="city"
          >
            {cities.map(c => (
              <option key={c.city_id} value={c.city_id} title={c.city_id}>{cityOptionLabel(c)}</option>
            ))}
          </SelectWithInlineCreate>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "City is required."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
      <AddCityModal
        nested
        hideTrigger
        show={addCityOpen}
        onHide={() => setAddCityOpen(false)}
        onCityCreated={async (data) => {
          await load();
          const id = data?.city_id;
          if (id) setCity(id);
          setAddCityOpen(false);
        }}
      />
    </>
  );
}

export function EditProfessionalAddressModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [address, setAddress] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setAddress(professional.address || ""); }, [show, professional]);

  const handleSave = async () => {
    setBusy(true);
    try {
      const payload = { address: address.trim() || null };
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", payload, { headers: getAuthHeaders() });
      Swal.fire("Success", "Address updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update address";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Address</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Address</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Address:</Form.Label>
            <Form.Control value={address} onChange={(e) => setAddress(e.target.value)} maxLength={80} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalEmailModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const isValidEmail = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  useEffect(() => { if (show) setEmail(professional.email || ""); }, [show, professional]);
  const isValid = !email || isValidEmail(email.trim());

  const handleSave = async () => {
    if (!isValid) return; setBusy(true);
    try {
      const payload = { email: (email.trim() === "" ? null : email.trim()) };
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", payload, { headers: getAuthHeaders() });
      Swal.fire("Success", "Email updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update email";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit E-mail</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit E-mail</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>E-mail:</Form.Label>
            <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Enter a valid e-mail."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalPhoneModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setPhone(professional.phone || ""); }, [show, professional]);
  const isValid = isOptionalPhoneValid(phone);

  const handleSave = async () => {
    if (!isValid) return; setBusy(true);
    try {
      const payload = { phone: formatPhoneForApi(phone) };
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", payload, { headers: getAuthHeaders() });
      Swal.fire("Success", "Phone updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update phone";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Telephone</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Telephone</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Telephone:</Form.Label>
            <PhoneInput
              country={'gr'}
              value={phone}
              onChange={(val) => setPhone(val)}
              enableSearch
              countryCodeEditable={false}
              inputProps={{ name: 'phone' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Max 15 digits."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalMobileModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [mobile, setMobile] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setMobile(professional.mobile || ""); }, [show, professional]);
  const isValid = isRequiredPhoneValid(mobile);

  const handleSave = async () => {
    if (!isValid) return; setBusy(true);
    try {
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", { mobile: formatPhoneForApi(mobile) }, { headers: getAuthHeaders() });
      Swal.fire("Success", "Mobile updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update mobile";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Cell phone</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Cell phone</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Cell phone *:</Form.Label>
            <PhoneInput
              country={'gr'}
              value={mobile}
              onChange={(val) => setMobile(val)}
              enableSearch
              countryCodeEditable={false}
              inputProps={{ name: 'mobile' }}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "Cell phone is required (max 15)."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalReliabilityModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [reliability, setReliability] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setReliability(professional.reliability || ""); }, [show, professional]);

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", { reliability: reliability || null }, { headers: getAuthHeaders() });
      Swal.fire("Success", "Reliability updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update reliability";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Reliability</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Reliability</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Reliability:</Form.Label>
            <AutocompleteSelect value={reliability} onChange={setReliability} placeholder="Select">
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </AutocompleteSelect>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalActiveModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [active, setActive] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setActive(!!professional.active); }, [show, professional]);

  const handleSave = async () => {
    setBusy(true);
    try {
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", { active }, { headers: getAuthHeaders() });
      Swal.fire("Success", "Active status updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update active status";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Active</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Active Status</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Check type="checkbox" label="Active" checked={active} onChange={(e) => setActive(e.target.checked)} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalAfmModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [afm, setAfm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setAfm(professional.afm || ""); }, [show, professional]);
  const isValid = !afm || (afm.length === 9 && /^\d{9}$/.test(afm));

  const handleSave = async () => {
    if (!isValid) return;
    setBusy(true);
    try {
      const payload = { afm: afm.trim() || null };
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", payload, { headers: getAuthHeaders() });
      Swal.fire("Success", "AFM updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.afm?.[0] || e?.response?.data?.detail || e?.response?.data || "Failed to update AFM";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit AFM</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit AFM</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>AFM:</Form.Label>
            <Form.Control
              value={afm}
              onChange={(e) => setAfm(e.target.value.replace(/\D/g, "").slice(0, 9))}
              maxLength={9}
              placeholder="e.g., 123456789"
              isInvalid={!isValid}
            />
            <Form.Text className="text-muted">Must be exactly 9 digits (leave empty if not applicable)</Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: isValid ? "green" : "red" }}>{isValid ? "Looks good." : "AFM must be exactly 9 digits."}</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={!isValid || busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalBankDetailsModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [bankDetails, setBankDetails] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setBankDetails(professional.bank_details || ""); }, [show, professional]);

  const handleSave = async () => {
    setBusy(true);
    try {
      const payload = { bank_details: htmlForApi(bankDetails) };
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", payload, { headers: getAuthHeaders() });
      Swal.fire("Success", "Bank details updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update bank details";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Bank details</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Bank details</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Bank details:</Form.Label>
            <RichTextEditor
              rows={4}
              value={bankDetails}
              onChange={setBankDetails}
              placeholder="e.g., Bank name, IBAN, account holder"
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}

export function EditProfessionalNotesModal({ professional, update_state }) {
  const [show, setShow] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (show) setNotes(professional.notes || ""); }, [show, professional]);

  const handleSave = async () => {
    setBusy(true);
    try {
      const payload = { notes: htmlForApi(notes) };
      const res = await axios.put(UPDATE_PRO + professional.professional_id + "/", payload, { headers: getAuthHeaders() });
      Swal.fire("Success", "Notes updated successfully", "success");
      update_state && update_state(res.data);
      setShow(false);
    } catch (e) {
      const apiMsg = e?.response?.data?.detail || e?.response?.data || "Failed to update notes";
      Swal.fire("Error", apiMsg, "error");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size="tiny" basic onClick={() => setShow(true)}><FiEdit style={{ marginRight: 6 }} /> Edit Notes</Button>
      <GuardedModal show={show} onHide={() => setShow(false)} centered>
        <Modal.Header closeButton><Modal.Title>Edit Notes</Modal.Title></Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Notes:</Form.Label>
            <RichTextEditor rows={3} value={notes} onChange={setNotes} />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <small className="mr-auto"><div style={{ color: "green" }}>Looks good.</div></small>
          <Button color="red" onClick={() => setShow(false)} disabled={busy}>Cancel</Button>
          <Button color="green" onClick={handleSave} disabled={busy}>{busy ? "Saving..." : "Save Changes"}</Button>
        </Modal.Footer>
      </GuardedModal>
    </>
  );
}


